import { ReactElement, useMemo } from 'react'
import { IWayPointAndPeriod } from '../../../Entity/interfaces'
import { Box, Typography } from '@mui/material'
import { MRT_ColumnDef } from 'material-react-table'
import SimpleMRTTable from '../../../Common/SimpleTableNonInfinite'
import { useTranslation } from 'react-i18next'
import {
    formatDistance,
    formatTransportWeight,
} from '../../../../utils/MiscellaneousUtils'
import KeyValueGrid, { IKeyValue } from '../../../Common/KeyValueGrid'
import { getAttributeById } from '../../../../utils/AttributeUtils'
import { useAppSelector } from '../../../../redux/hook'
import { TransportWeight } from '../../../Applications/Intransit/interfaces'
import { defaultTransportWeight } from '../../../../constants/constants'
import TruncatedText from '../../../Common/TruncatedText'

interface ICarbonReport {
    carbonEmissionsTableDataFromWaypoints: IWayPointAndPeriod[]
}

const CarbonReportFromWaypoints = (props: ICarbonReport): ReactElement => {
    const { t } = useTranslation()
    const { carbonEmissionsTableDataFromWaypoints } = props
    const entityData = useAppSelector(
        (state) => state.entityInstanceView.entityData,
    )
    const userPreferences = useAppSelector(
        (state) => state.user.user?.preferences,
    )
    const weightUnit = userPreferences?.units?.Weight || 'kg'

    const carbonEmissionsTableColumnsFromWaypoints = useMemo<
        MRT_ColumnDef<IWayPointAndPeriod>[]
    >(
        () => [
            {
                accessorKey: 'origin',
                header: t('origin'),
                accessorFn: (row) => <TruncatedText text={row.origin} />,
            },
            {
                accessorKey: 'destination',
                header: t('destination'),
                accessorFn: (row) => <TruncatedText text={row.destination} />,
            },
            {
                accessorKey: 'modeOfTransport',
                header: t('modeOfTransport'),
                accessorFn: (row) => t(row.modeOfTransport || ''),
            },
            {
                accessorKey: 'distanceFromPreviousLocation',
                header: t('distance'),
                accessorFn: (row) =>
                    formatDistance(row.distanceFromPreviousLocation),
            },
            {
                accessorKey: 'carbonEmission',
                header: t('Carbon Emissions'),
                accessorFn: (row) =>
                    row.carbonEmission
                        ? `${row.carbonEmission.toFixed(1)} ${'kg CO₂e'}`
                        : '',
            },
        ],
        [t],
    )

    const totalEmissions: number = carbonEmissionsTableDataFromWaypoints.reduce(
        (acc, row) => acc + (row.carbonEmission || 0),
        0,
    )

    const transportWeightAttribute = getAttributeById(
        entityData?.selfEntityAttributeData?.attributes || [],
        'transportWeight',
    )
    const transportWeight: TransportWeight = useMemo(() => {
        return {
            value:
                transportWeightAttribute?.value ||
                defaultTransportWeight[
                    weightUnit as keyof typeof defaultTransportWeight
                ],
            units: transportWeightAttribute?.unit || weightUnit,
        }
    }, [transportWeightAttribute, weightUnit])

    const keyValueData: IKeyValue[] = useMemo(
        () => [
            {
                key: t('totalEmissions'),
                value: `${totalEmissions.toFixed(1)} ${'kg CO₂e'}`,
                type: 'STRING',
            },
            {
                key: t('transportWeight'),
                value: formatTransportWeight(transportWeight),
                type: 'STRING',
            },
        ],
        [totalEmissions, t, transportWeight],
    )

    return (
        <Box sx={{ padding: '16px', display: 'flex', flexDirection: 'column' }}>
            <KeyValueGrid data={keyValueData} />
            <Box sx={{ marginTop: '20px' }}>
                <Typography
                    component="h3"
                    variant="h6"
                    sx={{ marginBottom: '4px' }}
                >
                    {t('carbonEmissions')}
                </Typography>
                <SimpleMRTTable
                    data={carbonEmissionsTableDataFromWaypoints || []}
                    columns={carbonEmissionsTableColumnsFromWaypoints}
                />
            </Box>
        </Box>
    )
}

export default CarbonReportFromWaypoints
